import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../contexts/I18nContext';
import { 
  LayoutDashboard, FileUp, Files, Car, ShoppingCart, Receipt, 
  BookOpen, Search, Settings, User, LogOut, Menu, X, Bell,
  Wallet, ChevronDown, Shield, Wrench, Database, Clock, Eye, EyeOff, LifeBuoy,
  GitCompare, Gauge
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';

const DashboardLayout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const { t, locale, setLocale, availableLocales } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [discreetMode, setDiscreetMode] = useState(user?.discreet_mode || false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const mainNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { path: '/upload', icon: FileUp, label: t('nav.upload') },
    { path: '/files', icon: Files, label: t('nav.files') },
    { path: '/support', icon: LifeBuoy, label: 'Support' },
    { path: '/vehicles', icon: Car, label: t('nav.vehicles') },
    { path: '/shop', icon: ShoppingCart, label: t('nav.shop') },
  ];

  const toolsNavItems = [
    { path: '/dtc', icon: Search, label: t('nav.dtcSearch') },
    { path: '/bosch', icon: Database, label: t('nav.boschSearch') },
    { path: '/kb', icon: BookOpen, label: t('nav.knowledgeBase') },
    { path: '/dyno', icon: Gauge, label: 'Virtual Dyno' },
    ...(user?.role === 'admin' || user?.role === 'operator' ? [
      { path: '/compare', icon: GitCompare, label: 'Dateivergleich' },
    ] : []),
  ];

  const accountNavItems = [
    { path: '/wallet', icon: Wallet, label: t('nav.wallet') },
    { path: '/invoices', icon: Receipt, label: t('nav.invoices') },
    { path: '/tools', icon: Wrench, label: t('nav.tools') },
    { path: '/profile', icon: User, label: t('nav.profile') },
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatBalance = (balance) => {
    if (discreetMode) return '••••••';
    return `${(balance || 0).toFixed(1)} Cr`;
  };

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        data-testid={`nav-${item.path.replace('/', '')}`}
        className={`sidebar-link ${isActive ? 'active' : ''}`}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
        {item.badge && (
          <Badge variant="secondary" className="ml-auto text-xs">{item.badge}</Badge>
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-[#1A1D23] flex">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-[#23272F]/80 backdrop-blur-xl border-r border-white/5
        transform transition-transform duration-300 lg:translate-x-0 lg:static
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-8 h-8 bg-[#EC2229] rounded-lg flex items-center justify-center">
              <span className="font-heading font-bold text-white text-sm">TX</span>
            </div>
            <span className="font-heading font-bold text-white text-lg">TrXL</span>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-6 overflow-y-auto h-[calc(100vh-4rem)]">
          {/* Main Nav */}
          <div>
            <p className="label-text px-4 mb-2">Main</p>
            <div className="space-y-1">
              {mainNavItems.map(item => (
                <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
              ))}
            </div>
          </div>

          {/* Tools */}
          <div>
            <p className="label-text px-4 mb-2">Tools</p>
            <div className="space-y-1">
              {toolsNavItems.map(item => (
                <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
              ))}
            </div>
          </div>

          {/* Account */}
          <div>
            <p className="label-text px-4 mb-2">Account</p>
            <div className="space-y-1">
              {accountNavItems.map(item => (
                <NavLink key={item.path} item={item} onClick={() => setSidebarOpen(false)} />
              ))}
            </div>
          </div>

          {/* Admin */}
          {isAdmin && (
            <div>
              <p className="label-text px-4 mb-2">Admin</p>
              <div className="space-y-1">
                <NavLink 
                  item={{ path: '/admin', icon: Shield, label: t('nav.admin') }}
                  onClick={() => setSidebarOpen(false)}
                />
                <NavLink 
                  item={{ path: '/admin/file-search', icon: Database, label: 'Datei-Suche' }}
                  onClick={() => setSidebarOpen(false)}
                />
              </div>
            </div>
          )}
        </nav>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="h-16 bg-[#1A1D23]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-30">
          <div className="h-full px-4 lg:px-6 flex items-center justify-between">
            {/* Left */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden text-slate-400 hover:text-white"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
              
              {/* Service Status */}
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 glass-card">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs text-slate-400">Service Online</span>
              </div>

              {/* Time */}
              <div className="hidden md:flex items-center gap-2 text-slate-400 text-sm">
                <Clock className="w-4 h-4" />
                <span className="font-mono">{currentTime.toLocaleTimeString(locale)}</span>
              </div>
            </div>

            {/* Right */}
            <div className="flex items-center gap-3">
              {/* Discreet Mode Toggle */}
              <button
                onClick={() => setDiscreetMode(!discreetMode)}
                className="p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                title={discreetMode ? 'Show values' : 'Hide values'}
                data-testid="discreet-mode-btn"
              >
                {discreetMode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>

              {/* Balance */}
              <Link 
                to="/wallet"
                className="hidden sm:flex items-center gap-2 px-4 py-2 glass-card hover:border-[#EC2229]/30 transition-colors"
                data-testid="balance-display"
              >
                <Wallet className="w-4 h-4 text-[#EC2229]" />
                <span className="font-mono font-semibold text-white">{formatBalance(user?.balance)}</span>
              </Link>

              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="relative p-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    data-testid="notifications-btn"
                  >
                    <Bell className="w-5 h-5" />
                    {notifications.length > 0 && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-[#EC2229] rounded-full" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 bg-[#23272F] border-white/10">
                  <div className="p-3 border-b border-white/5">
                    <p className="font-semibold text-white">Notifications</p>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-slate-400 text-sm">
                      No new notifications
                    </div>
                  ) : (
                    notifications.slice(0, 5).map(n => (
                      <DropdownMenuItem key={n.id} className="p-3">
                        <div>
                          <p className="text-sm text-white">{n.title}</p>
                          <p className="text-xs text-slate-400">{n.message}</p>
                        </div>
                      </DropdownMenuItem>
                    ))
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Language Selector */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-1 px-3 py-2 text-slate-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                    data-testid="language-selector"
                  >
                    <span className="text-lg">{availableLocales.find(l => l.code === locale)?.flag}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#23272F] border-white/10">
                  {availableLocales.map(l => (
                    <DropdownMenuItem 
                      key={l.code}
                      onClick={() => setLocale(l.code)}
                      className={locale === l.code ? 'bg-white/5' : ''}
                    >
                      <span className="mr-2">{l.flag}</span>
                      <span>{l.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-3 py-2 hover:bg-white/5 rounded-lg transition-colors"
                    data-testid="user-menu-btn"
                  >
                    <div className="w-8 h-8 rounded-full bg-[#EC2229]/20 flex items-center justify-center">
                      <span className="text-[#EC2229] font-semibold text-sm">
                        {user?.name?.charAt(0).toUpperCase() || 'U'}
                      </span>
                    </div>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-white">{user?.name}</p>
                      <p className="text-xs text-slate-400">{user?.role}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 bg-[#23272F] border-white/10">
                  <div className="p-3 border-b border-white/5">
                    <p className="font-medium text-white">{user?.name}</p>
                    <p className="text-xs text-slate-400">{user?.email}</p>
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="w-4 h-4 mr-2" />
                    {t('nav.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    {t('nav.settings')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/5" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
